import React, { FC } from 'react';
import { css } from '@emotion/core';
import { CapabilityDTO, TeamInputAtlassianOrgStatusEnum } from '@atlassiansox/phobos-client-ts';

import {
  flex,
  flexSpace,
  SPACE_COZY,
  flexStart,
  linkStyles,
  SPACE_COMPACT,
  spaceTopCompact,
  SPACE_MINIMUM,
} from '@/styles';
import {
  KeyValue,
  CapabilityTier,
  TeamEntityLink,
  ProductEntityLink,
  HelpTooltip,
} from '@/components/primitive';
import { DAC_DOCS_BASE_URL, DIRECTORY_BASE_URL } from '@/constants';

import TeamOrgStatus from './team-org-model/team-org-status';

type IProps = Pick<CapabilityDTO, 'team' | 'product' | 'owner' | 'importance'>;

const aotStyle = css`
  font-weight: 600;
`;

const pendingEntity = {
  name: 'Pending',
  publicId: 'x',
  atlassianOrgId: '',
  atlassianOrgStatus: TeamInputAtlassianOrgStatusEnum.Unassigned,
  atlassianOrgName: '',
  atlassianOrgLeader: '',
};

const CapabilityMetaInfo: FC<IProps> = ({
  product = pendingEntity,
  team = pendingEntity,
  owner,
  importance,
  ...other
}) => (
  <div
    css={[
      flex,
      flexSpace,
      css`
        width: 100%;
        margin: ${SPACE_COZY}px 0;
      `,
    ]}
    {...other}
  >
    <div
      css={[
        flexStart,
        css`
          gap: ${SPACE_COMPACT}px ${SPACE_COZY}px;
        `,
      ]}
    >
      <KeyValue title="Capability Tier">
        <CapabilityTier tier={importance} />
      </KeyValue>
      <KeyValue title="Product">
        <ProductEntityLink
          name={product.name}
          publicId={product.publicId}
          shouldTruncate
          truncateLength="650px"
        />
      </KeyValue>
      <KeyValue title="Team">
        <TeamEntityLink
          name={team.name}
          publicId={team.publicId}
          shouldTruncate
          truncateLength="650px"
        />
      </KeyValue>
      {owner ? (
        <KeyValue title="Owner">
          <a href={`${DIRECTORY_BASE_URL}/${owner}`} css={linkStyles}>
            <code>@{owner}</code>
          </a>
        </KeyValue>
      ) : null}
    </div>
    {team.atlassianOrgId && (
      <div
        css={[
          flexStart,
          spaceTopCompact,
          css`
            gap: ${SPACE_COMPACT}px ${SPACE_COZY}px;
          `,
        ]}
      >
        <KeyValue title="Atlassian Org Team">
          <div
            css={[
              flexStart,
              css`
                gap: ${SPACE_MINIMUM}px;
              `,
            ]}
          >
            <div css={aotStyle}>
              <TeamEntityLink
                name={team.atlassianOrgName || ''}
                publicId={team.publicId}
                shouldTruncate={false}
              />
            </div>
            <TeamOrgStatus orgStatus={team.atlassianOrgStatus as TeamInputAtlassianOrgStatusEnum} />
            {team.atlassianOrgStatus === TeamInputAtlassianOrgStatusEnum.Assigned && (
              <HelpTooltip>
                <>
                  Verify your team with an Atlassian Organisation Team.
                  <br />
                  Check out our{' '}
                  <a
                    href={`${DAC_DOCS_BASE_URL}teams/#connecting-an-atlassian-org-team-to-a-tome-team`}
                  >
                    docs
                  </a>
                  .
                </>
              </HelpTooltip>
            )}
          </div>
        </KeyValue>
        {team.atlassianOrgLeader && (
          <KeyValue title="Leader">
            <a href={`${DIRECTORY_BASE_URL}/${team.atlassianOrgLeader}`} css={linkStyles}>
              <code>@{team.atlassianOrgLeader}</code>
            </a>
          </KeyValue>
        )}
      </div>
    )}
  </div>
);

export default CapabilityMetaInfo;
